import {
  CONVERSATION_MIN_SHARE,
  CONVERSATION_MIN_WIDTH,
  DOCUMENT_VIEWER_MIN_WIDTH,
  NAVIGATION_RAIL_WIDTH,
} from '@/utils/constants';

import type { TDocumentViewerLayout, TDocumentViewerSpace } from './types';

/**
 * Keeps a width inside an inclusive range. `min` is applied last so a range narrower than `min` still
 * yields `max`, which is what happens when there is less room than the Viewer would like.
 */
const clamp = (value: number, min: number, max: number) => Math.max(Math.min(value, max), Math.min(min, max));

/**
 * Decides how the document viewer shares the page with the conversation section.
 */
export const resolveDocumentViewerLayout = ({
  windowWidth,
  historyWidth,
}: TDocumentViewerSpace): TDocumentViewerLayout => {
  const availableWidth = Math.max(0, windowWidth - NAVIGATION_RAIL_WIDTH - historyWidth);
  const conversationMinWidth = Math.max(CONVERSATION_MIN_WIDTH, Math.ceil(availableWidth * CONVERSATION_MIN_SHARE));
  const widthBesideConversation = availableWidth - conversationMinWidth;

  const maxWidth = Math.max(0, widthBesideConversation);
  const minWidth = Math.min(DOCUMENT_VIEWER_MIN_WIDTH, maxWidth);

  return {
    fits: widthBesideConversation >= DOCUMENT_VIEWER_MIN_WIDTH,
    availableWidth,
    minWidth,
    maxWidth,
    openingWidth: clamp(Math.round(availableWidth / 2), minWidth, maxWidth),
  };
};

/**
 * Holds a width the user chose inside the limits the current layout allows, so the conversation keeps its
 * floor when the window shrinks or the thread history opens or widens.
 */
export const clampDocumentViewerWidth = (width: number, { minWidth, maxWidth }: TDocumentViewerLayout) =>
  clamp(width, minWidth, maxWidth);
